import {
	ChangeDetectorRef,
	Component,
	DoCheck,
	inject,
	KeyValueDiffer,
	KeyValueDiffers,
	OnDestroy,
	OnInit,
} from '@angular/core';
import { Validators } from '@angular/forms';
import {
	alinharDado,
	baixarEAbrirPDF,
	criarImagemBase64FromChart,
	enumAsSelectOptions,
	isNuloOuUndefined,
	numberToBrNumber,
	numberToBrNumberTables,
	obter_erro_request,
	verificaSePDFEAdicionaNaTabela,
} from '@utils';
import { Chart, SeriesOptionsType } from 'highcharts';
import { ToastrService } from 'ngx-toastr';
import {
	AgrupamentoEspecial,
	DadosDesvioRelatorio,
	RelatorioDesvio,
} from '../../../interfaces';
import {
	FormularioRelatorio,
	INPUTS_RELATORIOS,
} from '../../../interfaces/tipos-relatorios';
import { RelatoriosService } from '../../../services/relatorios.service';
import { optionsChartDesvio } from './chart-options';
import {
	COR_REGIAO_INDIVIDUAL,
	corrigeDuplicacaoNome,
	gerarFilenameGrafico,
	obterLabelPeriodo,
} from '../../../utils';
import { format } from 'date-fns';
import * as pdfseira from '@utils/pdf-seira';
import { TDocumentDefinitions } from 'pdfmake/interfaces';
import { GroupButton } from '@componentes/public-button-group/public-button-group.component';
import { DocumentExporter } from '@utils/document-exporter';
import { DateTimeUtils } from '@utils/datetime-util';
import * as ExcelTable from 'mr-excel';
import { Observable, Subject, takeUntil } from 'rxjs';
import { colors } from '@shared/interfaces/paleta-cores';
import { Select } from '@layout/interfaces/select';
import { NumberUtils } from '@utils/number-utils';
import {
	getCorPorRegiao,
	normalizarNome,
	ORDEM_REGIOES_PLUVIOMETRICAS,
} from '../../../utils/grafico-regioes-utils';
import { isEstadoCompleto } from '../../../utils/agrupamento-utils';

@Component({
	selector: 'seira-grafico-desvio-milimetro',
	templateUrl: './grafico-desvio.component.html',
	styleUrls: ['./grafico-desvio.component.scss'],
})
export class GraficoDesvioComponent implements DoCheck, OnDestroy, OnInit {
	inputs = inject(INPUTS_RELATORIOS);
	opcoesGrafico: Highcharts.Options = optionsChartDesvio;
	grafico?: Highcharts.Chart;
	relatorios: RelatorioDesvio[] = [];
	private differ: KeyValueDiffer<any, any>;
	fileName: string;
	descricaoRelatorioMilimetro =
		'Define-se como desvio (mm), a diferença entre a pluviometria registrada e a sua climatologia para um mesmo período, em um determinado local ou região.';
	descricaoRelatorioPorcentagem =
		'Define-se como desvio (%), a variação da pluviometria em relação à climatologia, expresso em porcentagem, indicando se o acumulado de chuva foi superior ou inferior à climatologia.';

	get descricaoRelatorio(): string {
		return this.isDesvioPorcentagem
			? this.descricaoRelatorioPorcentagem
			: this.descricaoRelatorioMilimetro;
	}

	periodoTitulo = '';
	_destroyed = new Subject();
	loading = true;
	resultado = false;
	tipoDesvio = '';
	tipoPeriodo = this.inputs.form.get(FormularioRelatorio.PERIODO_BUSCA);
	botoesDeExportacao: GroupButton[] = [
		{
			label: '.png',
			size: 'small',
			icon: 'ph-image',
			onClick: () => this.exportarPNG(),
		},
		{
			label: '.pdf',
			size: 'small',
			icon: 'ph-file-pdf',
			onClick: () => this.exportarPDF(),
		},
		{
			label: '.csv',
			size: 'small',
			icon: 'ph-file-csv',
			onClick: () => this.exportarCSV(),
		},
		{
			label: '.txt',
			size: 'small',
			icon: 'ph-file-text',
			onClick: () => this.exportarTXT(),
		},
		{
			label: '.xlsx',
			size: 'small',
			icon: 'ph-file-xls',
			onClick: () => this.exportarXLSX(),
		},
	];
	tituloArquivoExportacao = '';
	desviosPorAno: DadosDesvioRelatorio[];
	agrupamento: Select[] | any = [];
	constructor(
		private readonly relatorioService: RelatoriosService,
		private readonly differs: KeyValueDiffers,
		private readonly cdr: ChangeDetectorRef,
		private toastr: ToastrService
	) {
		this.differ = this.differs.find({}).create();
		this.agrupamento = enumAsSelectOptions(AgrupamentoEspecial);
	}

	ngOnInit(): void {
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValue('mensal', { emitEvent: false });

		this.setValidatorsByAgrupamento(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
		this.setValidators();
	}

	ngOnDestroy(): void {
		this.clearValidators();
		this._destroyed.next(undefined);
	}

	setGrafico(grafico: Chart): void {
		this.grafico = grafico;
		this.configurarGrafico();
	}

	getAno(dataString: string | Date): number {
		return new Date(dataString).getUTCFullYear();
	}

	get isSeparadoPorEstacoes(): boolean {
		return ['MUNICIPIO_POSTO'].includes(
			this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value
		);
	}

	get _agrupamento(): string {
		return this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.value;
	}

	public calcularDesvioMilimetro(chuva: number, climatologia: number): number {
		const desvio = chuva - climatologia;
		return parseFloat(desvio.toFixed(2));
	}

	public calcularDesvioPorcentagem(
		chuva: number,
		climatologia: number
	): number {
		if (climatologia === 0) {
			return 0;
		}
		const desvioPercentual = ((chuva - climatologia) / climatologia) * 100;
		return Number(numberToBrNumber(desvioPercentual, 1)!.replaceAll(',', '.'));
	}

	private getCorSerie(
		nomeSerie: string,
		fallbackIndex: number
	): string | undefined {
		if (!isEstadoCompleto(this.inputs.form, this._agrupamento)) {
			return COR_REGIAO_INDIVIDUAL;
		}
		if (this._agrupamento === 'MESORREGIAO') {
			return getCorPorRegiao(
				nomeSerie,
				this.inputs.mesorregioes,
				this._agrupamento
			);
		}
		if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
			return getCorPorRegiao(nomeSerie, this.inputs.regioes, this._agrupamento);
		}

		return colors[fallbackIndex % colors.length];
	}

	getSeriesSeparadasPorAno(relatorios: RelatorioDesvio[]): SeriesOptionsType[] {
		const relatoriosOrdenados = [...relatorios].sort((a, b) =>
			this.getNomeSerie(a).localeCompare(this.getNomeSerie(b), 'pt-BR', {
				sensitivity: 'base',
			})
		);

		return relatoriosOrdenados.map((r, idx) => {
			const anos = new Set(r.desvios.map(d => this.getAno(d.data)));
			const desvios: DadosDesvioRelatorio[] = [];
			anos.forEach(ano => {
				const desviosDoAnoOriginal = r.desvios.filter(
					d => this.getAno(d.data) === ano
				);

				let desviosConsideradosParaSoma = desviosDoAnoOriginal;

				const hoje = new Date();
				const anoCorrente = hoje.getUTCFullYear();

				if (ano === anoCorrente) {
					const mesCorrente = hoje.getUTCMonth();
					desviosConsideradosParaSoma = desviosDoAnoOriginal.filter(
						itemDesvioMensal => {
							const dataDoItemMensal = new Date(itemDesvioMensal.data);
							return dataDoItemMensal.getUTCMonth() < mesCorrente;
						}
					);
				}

				const objChuvaEClimaSomados =
					desviosConsideradosParaSoma.reduce<DadosDesvioRelatorio>(
						(a, b) => ({
							desvioMilimetro: 0,
							chuva: a.chuva + b.chuva,
							desvioPorcentagem: 0,
							climatologia: a.climatologia + b.climatologia,
							data: new Date(ano, 0, 1),
						}),
						{
							desvioMilimetro: 0,
							chuva: 0,
							desvioPorcentagem: 0,
							climatologia: 0,
							data: new Date(ano, 0, 1),
						}
					);

				const { chuva, climatologia, data } = objChuvaEClimaSomados;
				const dadosAnuaisDesvio = {
					chuva: chuva,
					climatologia: climatologia,
					data: data,
					desvioMilimetro: this.calcularDesvioMilimetro(chuva, climatologia),
					desvioPorcentagem: this.calcularDesvioPorcentagem(
						chuva,
						climatologia
					),
				};
				desvios.push(dadosAnuaisDesvio);
				this.desviosPorAno = desvios;
			});
			return {
				type: 'column',
				name: this.getNomeSerie(r),
				color: this.getCorSerie(this.getNomeSerie(r), idx),
				groupPadding: 0.1,
				pointPadding: 0.1,
				data: desvios.map(d =>
					this.isDesvioPorcentagem ? d.desvioPorcentagem : d.desvioMilimetro
				),
			};
		});
	}

	getNomeSerie(r: RelatorioDesvio): string {
		const mapeamento: Record<string, string> = {
			REGIAO_PLUVIOMETRICA: r.regiaoPluviometrica,
			MUNICIPIO: r.municipio,
			MICRORREGIAO: r.microrregiao,
			BACIA_SUBBACIA: this.getNomeBaciaSubBacia(r),
			MESORREGIAO: r.mesorregiao,
		};
		let name = '';
		if (this.isSeparadoPorEstacoes) {
			name = r.estacao;
		} else {
			name = mapeamento[this._agrupamento];
		}
		return name;
	}

	private getNomeBaciaSubBacia(
		relatorio: Pick<RelatorioDesvio, 'bacia' | 'subBacia'>
	): string {
		if (relatorio.bacia && relatorio.subBacia) {
			return `${relatorio.bacia} / ${relatorio.subBacia}`;
		}

		return relatorio.bacia || relatorio.subBacia || '';
	}

	configurarGrafico(): void {
		const tipoPeriodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;
		const categories: string[] = [];
		let series: SeriesOptionsType[];
		if (tipoPeriodo === 'anual' || tipoPeriodo === 'anual_parcial') {
			this.fileName = gerarFilenameGrafico(
				`desvio_${this.isDesvioPorcentagem ? '%' : 'mm'}_anual`
			);
			series = this.getSeriesSeparadasPorAno(this.relatorios);
			const years = new Set(
				this.relatorios.flatMap(r =>
					r.desvios.map(d => this.getAno(d.data).toString())
				)
			);

			categories.push(...years);
		} else {
			if (tipoPeriodo === 'mensal') {
				this.fileName = gerarFilenameGrafico(
					`desvio_${this.isDesvioPorcentagem ? '%' : 'mm'}_mensal`
				);
			}
			const relatorios = [...this.relatorios].sort((a, b) => {
				const nomeA = this.getNomeSerie(a);
				const nomeB = this.getNomeSerie(b);

				if (this._agrupamento === 'REGIAO_PLUVIOMETRICA') {
					const ordemNormalizada = ORDEM_REGIOES_PLUVIOMETRICAS.map(n =>
						normalizarNome(n)
					);

					const indexA = ordemNormalizada.indexOf(normalizarNome(nomeA));
					const indexB = ordemNormalizada.indexOf(normalizarNome(nomeB));

					if (indexA !== -1 && indexB !== -1) return indexA - indexB;

					if (indexA !== -1) return -1;
					if (indexB !== -1) return 1;
				}

				return nomeA.localeCompare(nomeB, 'pt-BR', { sensitivity: 'base' });
			});

			series = relatorios.map((r, idx) => {
				const nomeSerie = this.getNomeSerie(r);

				return {
					type: 'column',
					name: nomeSerie,
					color: this.getCorSerie(nomeSerie, idx),
					groupPadding: 0.1,
					pointPadding: 0.1,
					data: r.desvios.map((d, dataIdx) => {
						if (idx === 0) {
							categories.push(
								format(
									new Date(d.data),
									`${
										tipoPeriodo !== 'anual' && tipoPeriodo !== 'anual_parcial'
											? 'MM/'
											: ''
									}yyyy`
								)
							);
						}

						return this.isDesvioPorcentagem
							? d.desvioPorcentagem
							: d.desvioMilimetro;
					}),
				};
			});
		}
		if (this.grafico) {
			this.grafico.update(
				{
					series,
					xAxis: {
						categories,
						title: {
							text:
								tipoPeriodo === 'anual' || tipoPeriodo === 'anual_parcial'
									? 'Anos'
									: 'Meses',
						},
					},
					yAxis: {
						title: {
							text: this.isDesvioPorcentagem ? 'Desvio (%)' : 'Desvio (mm)',
						},
					},
					tooltip: {
						valueSuffix: this.isDesvioPorcentagem ? ' %' : ' mm',
						valueDecimals: 1,
					},
				},
				true,
				true
			);
			this.grafico.setTitle({
				text: `Desvio ${this.isDesvioPorcentagem ? '(%)' : '(mm)'} - ${
					this.periodoTitulo
				}`,
				align: 'center',
			});

			if (this._agrupamento == 'REGIAO_PLUVIOMETRICA') {
				this.grafico.update({
					colors: [
						'#FFD600',
						'#00008B',
						'#90EE90',
						'#FF6D00',
						'#e81515',
						'#006400',
						'#5DADE2',
					],
				});
			} else {
				this.grafico.update({
					colors: optionsChartDesvio.colors,
				});
			}

			this.opcoesGrafico = {
				...this.opcoesGrafico,
				exporting: {
					enabled: false,
					buttons: {
						contextButton: {
							enabled: false,
						},
					},
					filename: this.fileName,
				},
			};

			this.grafico.update(
				{
					exporting: {
						enabled: false,
						buttons: {
							contextButton: {
								enabled: false,
							},
						},
					},
				},
				true,
				true
			);
		}
		this.cdr.detectChanges();
		this.differ.diff(this);
	}

	ngDoCheck(): void {
		const changes = this.differ.diff(this);
		if (changes && this.grafico) {
			this.grafico.reflow();
		}
	}

	clearValidators(): void {
		this.inputs.form.get(FormularioRelatorio.DATA_INICIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.AGRUPAMENTO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MUNICIPIO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.ESTACAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MICRORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.MESORREGIAO)?.clearValidators();
		this.inputs.form.get(FormularioRelatorio.BACIA)?.clearValidators();
		this.inputs.form
			.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
			?.clearValidators();
	}

	setValidatorsByAgrupamento(agrupamentoValue: string) {
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MUNICIPIO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.ESTACAO);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.MICRORREGIAO
		);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.MESORREGIAO);
		this.handleRemoveValidatorsFromFormControl(FormularioRelatorio.BACIA);
		this.handleRemoveValidatorsFromFormControl(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		);

		switch (agrupamentoValue) {
			case 'MUNICIPIO_POSTO':
				this.inputs.form
					?.get(FormularioRelatorio.ESTACAO)
					?.setValidators(Validators.required);
				break;
			case 'MICRORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MICRORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MESORREGIAO':
				this.inputs.form
					?.get(FormularioRelatorio.MESORREGIAO)
					?.setValidators(Validators.required);
				break;
			case 'MUNICIPIO':
				this.inputs.form
					?.get(FormularioRelatorio.MUNICIPIO)
					?.setValidators(Validators.required);
				break;
			case 'BACIA_SUBBACIA':
				this.inputs.form
					?.get(FormularioRelatorio.BACIA)
					?.setValidators(Validators.required);
				break;
			case 'REGIAO_PLUVIOMETRICA':
				this.inputs.form
					?.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)
					?.setValidators(Validators.required);
				break;
		}
	}

	setValidators() {
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.valueChanges.pipe(takeUntil(this._destroyed))
			.subscribe({
				next: value => {
					this.setValidatorsByAgrupamento(value);
				},
			});

		this.inputs.form
			.get(FormularioRelatorio.DATA_INICIO)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.DATA_FIM)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.PERIODO_BUSCA)
			?.setValidators(Validators.required);
		this.inputs.form
			.get(FormularioRelatorio.AGRUPAMENTO)
			?.setValidators(Validators.required);
	}

	handleRemoveValidatorsFromFormControl(data: FormularioRelatorio) {
		this.inputs.form.get(data)?.clearValidators();
		this.inputs.form.get(data)?.updateValueAndValidity({ emitEvent: false });
	}

	getNomeRegiaoPluviometricaSelecionada() {
		const regiaoSelecionada = this.inputs.form.get(
			FormularioRelatorio.REGIAO_PLUVIOMETRICA
		)?.value;

		return this.inputs.regioes.find(
			regiao => regiao.value?.toString() === regiaoSelecionada?.toString()
		)?.name;
	}

	getNomeMesorregiaoSelecionada() {
		const mesorregiaoSelecionada = this.inputs.form.get(
			FormularioRelatorio.MESORREGIAO
		)?.value;

		return this.inputs.mesorregioes.find(
			mesorregiao =>
				mesorregiao.value?.toString() === mesorregiaoSelecionada?.toString()
		)?.name;
	}

	getNomeBaciaSelecionada() {
		const baciaSelecionada = this.inputs.form.get(
			FormularioRelatorio.BACIA
		)?.value;

		return this.inputs.bacias.find(
			bacia => bacia.value?.toString() === baciaSelecionada?.toString()
		)?.name;
	}

	get dataFim(): Date {
		const dataFim = this.inputs.form.get(FormularioRelatorio.DATA_FIM)?.value;
		return dataFim;
	}

	get dataInicio(): Date {
		const dataInicio = this.inputs.form.get(
			FormularioRelatorio.DATA_INICIO
		)?.value;
		return dataInicio;
	}

	get isDesvioPorcentagem(): boolean {
		const tipo = this.inputs.form.get(FormularioRelatorio.TIPO)?.value;
		return tipo === 'DESVIO_PORCENTAGEM';
	}

	getDadosTabelaParaExportacao(dados: RelatorioDesvio[], isPdf: boolean) {
		const tableData: any[][] = [];
		this.tipoDesvio = this.inputs.form.get(FormularioRelatorio.TIPO)?.value;
		const tipoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const colunas: any[] = [];
		const periodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		colunas.push(
			{
				text: AgrupamentoEspecial[
					tipoAgrupamento as keyof typeof AgrupamentoEspecial
				],
				fillColor: '#DCDCDC',
				alignment: 'center',
			},
			{ text: 'Data', fillColor: '#DCDCDC', alignment: 'center' },
			{
				text:
					this.tipoDesvio === 'DESVIO_MILIMETRO' ? 'Desvio (mm)' : 'Desvio (%)',
				fillColor: '#DCDCDC',
				alignment: 'center',
			}
		);

		verificaSePDFEAdicionaNaTabela(tableData, colunas, isPdf);

		const getRowData = (
			item: RelatorioDesvio,
			desvio: DadosDesvioRelatorio
		) => {
			const agrupamentoMappings = {
				MUNICIPIO_POSTO:
					tipoAgrupamento === 'MUNICIPIO_POSTO'
						? corrigeDuplicacaoNome(item.municipio ? item.municipio : '')
						: alinharDado(
								corrigeDuplicacaoNome(item.municipio ? item.municipio : ''),
								isPdf
							),
				MUNICIPIO: alinharDado(item.municipio, isPdf),
				MICRORREGIAO: alinharDado(item.microrregiao, isPdf),
				MESORREGIAO: alinharDado(item.mesorregiao, isPdf),
				BACIA_SUBBACIA: alinharDado(this.getNomeBaciaSubBacia(item), isPdf),
				REGIAO_PLUVIOMETRICA: alinharDado(item.regiaoPluviometrica, isPdf),
				default: alinharDado(item.subBacia, isPdf),
			};

			const agrupamentoValue =
				agrupamentoMappings[
					tipoAgrupamento as keyof typeof agrupamentoMappings
				] || agrupamentoMappings.default;
			const formattedDate = DateTimeUtils.formatarData(
				DateTimeUtils.formatToZonedDateTime(desvio.data.toString()),
				periodo === 'mensal' ? 'MM/yyyy' : 'yyyy'
			);
			const desvioValue =
				this.tipoDesvio === 'DESVIO_MILIMETRO'
					? numberToBrNumberTables(desvio.desvioMilimetro, 1)
					: numberToBrNumberTables(desvio.desvioPorcentagem, 1);

			return [
				this._agrupamento === 'MUNICIPIO_POSTO'
					? agrupamentoValue
					: alinharDado(agrupamentoValue, isPdf),
				alinharDado(formattedDate, isPdf),
				alinharDado(desvioValue, isPdf),
			];
		};

		if (periodo === 'mensal') {
			dados.forEach((item: RelatorioDesvio) => {
				item.desvios.forEach(desvio =>
					tableData.push(getRowData(item, desvio))
				);
			});
		} else {
			this.getRelatorioDesviosPorAno(dados).forEach(desvioPorAno => {
				desvioPorAno.desvios.forEach(desvio =>
					tableData.push(getRowData(desvioPorAno, desvio))
				);
			});
		}

		return tableData;
	}

	retornarDesviosParaXLSX() {
		const tableData: any[] = [];
		const tipoDesvio = this.inputs.form.get(FormularioRelatorio.TIPO)?.value;
		const tipoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const periodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		const formatData = (data: string) =>
			DateTimeUtils.formatarData(
				DateTimeUtils.formatToZonedDateTime(data),
				periodo === 'mensal' ? 'MM/yyyy' : 'yyyy'
			);

		const pushData = (group: string, desvio: any, relatorio: any) => {
			const tipoPeriodo = this.inputs.form.get(
				FormularioRelatorio.PERIODO_BUSCA
			)?.value;
			const isAnual =
				tipoPeriodo === 'anual' || tipoPeriodo === 'anual_parcial';
			const chaveDesvio =
				tipoDesvio === 'DESVIO_MILIMETRO'
					? 'desvioMilimetro'
					: 'desvioPorcentagem';

			const valorBruto =
				tipoDesvio === 'DESVIO_MILIMETRO'
					? desvio.desvioMilimetro
					: desvio.desvioPorcentagem;

			const valorDesvio = isNuloOuUndefined(valorBruto)
				? '-'
				: NumberUtils.getIgnorandoCasasDecimais(valorBruto);

			const dataFormatada = isAnual
				? Number(desvio.data.getFullYear())
				: formatData(desvio.data.toString());

			const data = {
				data: dataFormatada,
				[group]: relatorio[group],
				[chaveDesvio]: valorDesvio,
			};
			tableData.push(data);
		};

		const groupMapping = {
			MUNICIPIO_POSTO: 'municipio',
			MUNICIPIO: 'municipio',
			MICRORREGIAO: 'microrregiao',
			MESORREGIAO: 'mesorregiao',
			BACIA_SUBBACIA: 'bacia',
			REGIAO_PLUVIOMETRICA: 'regiaoPluviometrica',
		};

		if (periodo === 'mensal') {
			this.relatorios.forEach(relatorio => {
				relatorio.desvios.forEach(desvio => {
					const group =
						groupMapping[tipoAgrupamento as keyof typeof groupMapping];
					if (group) {
						pushData(group, desvio, relatorio);
					}
				});
			});
		} else {
			this.getRelatorioDesviosPorAno(this.relatorios).forEach(relatorio => {
				relatorio.desvios.forEach(desvio => {
					const group =
						groupMapping[tipoAgrupamento as keyof typeof groupMapping];
					if (group) {
						pushData(group, desvio, relatorio);
					}
				});
			});
		}

		return tableData;
	}

	private getRelatorioDesviosPorAno(
		relatorios: RelatorioDesvio[]
	): RelatorioDesvio[] {
		const retorno = relatorios.map(r => {
			let desvios: DadosDesvioRelatorio[] = [];
			desvios = this.desviosPorAno;
			const relatorio: RelatorioDesvio = {
				...r,
				desvios,
			};
			return relatorio;
		});
		return retorno;
	}

	gerarRelatorio(): void {
		if (
			![
				'MUNICIPIO_POSTO',
				'MUNICIPIO',
				'MICRORREGIAO',
				'MESORREGIAO',
				'REGIAO_PLUVIOMETRICA',
				'BACIA_SUBBACIA',
			].includes(this._agrupamento)
		) {
			this.toastr.error(
				`A geração de gráfico de desvio não é permitida para este agrupamento`,
				'Opção não permitida'
			);
			return;
		}
		let chave: keyof typeof FormularioRelatorio | undefined;
		if (!['REGIAO_PLUVIOMETRICA'].includes(this._agrupamento)) {
			chave =
				this._agrupamento === 'MUNICIPIO_POSTO'
					? 'ESTACAO'
					: this._agrupamento === 'BACIA_SUBBACIA'
						? 'BACIA'
						: (this._agrupamento as keyof typeof FormularioRelatorio);
		}

		const periodoBusca = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		const dataInicioOriginalForm = this.dataInicio;
		const dataFimOriginalForm = new Date(this.dataFim.getTime());

		let dataFimParaTituloDisplay = new Date(dataFimOriginalForm.getTime());
		let periodoDatas = '';

		if (periodoBusca === 'anual') {
			const hoje = new Date();
			const anoCorrenteLocal = hoje.getFullYear();
			const mesCorrenteLocal = hoje.getMonth();

			if (dataFimOriginalForm.getFullYear() === anoCorrenteLocal) {
				const anoAjustado =
					mesCorrenteLocal === 0 ? anoCorrenteLocal - 1 : anoCorrenteLocal;
				const mesAjustado = mesCorrenteLocal === 0 ? 11 : mesCorrenteLocal - 1;
				dataFimParaTituloDisplay = new Date(anoAjustado, mesAjustado + 1, 0);

				const formatoData = 'dd/MM/yyyy';
				periodoDatas = `${format(
					dataInicioOriginalForm,
					formatoData
				)} a ${format(dataFimParaTituloDisplay, formatoData)}`;
			} else {
				periodoDatas = DateTimeUtils.formatarDataPeriodo(
					dataInicioOriginalForm,
					dataFimOriginalForm,
					periodoBusca
				);
			}
		} else {
			periodoDatas = DateTimeUtils.formatarDataPeriodo(
				dataInicioOriginalForm,
				dataFimOriginalForm,
				periodoBusca
			);
		}

		const labelPeriodo = obterLabelPeriodo(periodoBusca);
		this.periodoTitulo = `${labelPeriodo} (${periodoDatas})`;

		this.tituloArquivoExportacao = `Desvio ${
			this.isDesvioPorcentagem ? '(%)' : '(mm)'
		} - ${this.periodoTitulo}`;
		this.loading = true;
		this.inputs.setLoading(true);
		const periodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;
		const valorSelecionado =
			this._agrupamento === 'REGIAO_PLUVIOMETRICA'
				? this.inputs.form.get(FormularioRelatorio.REGIAO_PLUVIOMETRICA)?.value
				: chave
					? this.inputs.form.get(FormularioRelatorio[chave])?.value
					: null;
		const idEntidade =
			[
				'REGIAO_PLUVIOMETRICA',
				'MESORREGIAO',
				'BACIA_SUBBACIA',
				'MICRORREGIAO',
			].includes(this._agrupamento) && valorSelecionado === '0'
				? null
				: valorSelecionado;
		const consultaDesvio: Observable<RelatorioDesvio[]> =
			this.relatorioService.buscarRelatorioDesvio({
				agrupamento: this._agrupamento,
				idEntidade,
				dataFim: this.dataFim,
				dataInicio: this.dataInicio,
				periodo: periodo.toUpperCase(),
				incluirDetalhes: false,
			});
		consultaDesvio.subscribe({
			next: (value: RelatorioDesvio[]) => {
				const relatoriosBase = value;
				const nomeRegiaoSelecionada =
					this._agrupamento === 'REGIAO_PLUVIOMETRICA' && idEntidade
						? this.getNomeRegiaoPluviometricaSelecionada()
						: undefined;
				const nomeMesorregiaoSelecionada =
					this._agrupamento === 'MESORREGIAO' &&
					idEntidade &&
					idEntidade !== '0'
						? this.getNomeMesorregiaoSelecionada()
						: undefined;
				const nomeBaciaSelecionada =
					this._agrupamento === 'BACIA_SUBBACIA' &&
					idEntidade &&
					idEntidade !== '0'
						? this.getNomeBaciaSelecionada()
						: undefined;
				const relatoriosFiltrados = nomeRegiaoSelecionada
					? relatoriosBase.filter(
							item =>
								corrigeDuplicacaoNome(item.regiaoPluviometrica) ===
								corrigeDuplicacaoNome(nomeRegiaoSelecionada)
						)
					: nomeMesorregiaoSelecionada
						? relatoriosBase.filter(
								item =>
									corrigeDuplicacaoNome(item.mesorregiao) ===
									corrigeDuplicacaoNome(nomeMesorregiaoSelecionada)
							)
						: nomeBaciaSelecionada
							? relatoriosBase.filter(
									item =>
										corrigeDuplicacaoNome(item.bacia) ===
										corrigeDuplicacaoNome(nomeBaciaSelecionada)
								)
							: relatoriosBase;
				this.relatorioService.atualizaVisibilidadeBotoesExportacao(
					relatoriosFiltrados
				);

				if (relatoriosFiltrados.length === 0) {
					this.toastr.info(
						'Não há desvios para os parametros selecionados',
						'Sem dados'
					);
				}

				this.relatorios = relatoriosFiltrados;
			},
			error: (err: any) => {
				const msgErro = obter_erro_request(err);
				this.toastr.error(
					msgErro,
					'Erro ao obter informações de desvio no servidor'
				);
				this.loading = false;
				this.inputs.setLoading(false);
			},
			complete: () => {
				this.loading = false;
				this.inputs.setLoading(false);
			},
		});
	}
	private carregarImagem(src: string): Promise<HTMLImageElement> {
		return new Promise((resolve, reject) => {
			const img = new Image();
			img.crossOrigin = 'anonymous';
			img.onload = () => resolve(img);
			img.onerror = err => reject(err);
			img.src = src;
		});
	}

	private async getLogoAesaBase64(): Promise<string | null> {
		try {
			const logo = await this.carregarImagem('assets/images/logos/aesa.png');
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error('[DESVIO] Erro ao carregar logo AESA:', e);
			return null;
		}
	}
	private async getLogoGovernoBase64(): Promise<string | null> {
		try {
			const logo = await this.carregarImagem(
				'assets/images/logos/governo_pb.png'
			);
			const canvas = document.createElement('canvas');
			canvas.width = logo.width;
			canvas.height = logo.height;
			const ctx = canvas.getContext('2d')!;
			ctx.drawImage(logo, 0, 0);
			return canvas.toDataURL('image/png');
		} catch (e) {
			console.error('[DESVIO] Erro ao carregar logo Governo:', e);
			return null;
		}
	}

	async exportarPDF() {
		const chartsConverted: TDocumentDefinitions['content'] = [];

		if (!this.grafico) {
			this.toastr.error(
				'Não é possível gerar um PDF pois nenhum gráfico foi gerado.'
			);
			return;
		}

		chartsConverted.push({
			image: await criarImagemBase64FromChart(this.grafico),
			fontSize: 10,
			alignment: 'center',
			height: 400,
			width: 750,
		});

		const documentDefinition: any =
			await pdfseira.documentDefinitions('landscape');
		documentDefinition.pageOrientation = 'landscape';

		const logoAesaBase64 = await this.getLogoAesaBase64();
		const logoGovernoBase64 = await this.getLogoGovernoBase64();

		documentDefinition.images = documentDefinition.images || {};

		if (logoAesaBase64) {
			documentDefinition.images['aesaLogo'] = logoAesaBase64;
		}

		if (logoGovernoBase64) {
			documentDefinition.images['governoLogo'] = logoGovernoBase64;
		}

		documentDefinition.footer = (currentPage: number, pageCount: number) => {
			return {
				margin: [40, 0, 40, 20],
				columns: [
					logoAesaBase64
						? {
								image: 'aesaLogo',
								width: 60,
								alignment: 'left',
							}
						: {},
					{
						text: `Página ${currentPage} de ${pageCount}`,
						alignment: 'center',
						fontSize: 8,
					},
					logoGovernoBase64
						? {
								image: 'governoLogo',
								width: 90,
								alignment: 'right',
							}
						: {},
				],
			};
		};

		const conteudoTabela = this.getDadosTabelaParaExportacao(
			this.relatorios,
			true
		);

		const content: TDocumentDefinitions['content'] = [
			{
				text: '',
				margin: [0, 30, 0, 15],
			},
			...chartsConverted,
			{
				table: {
					widths: ['40%', '30%', '30%'],
					body: conteudoTabela,
				},
				marginTop: 50,
			},
		];

		documentDefinition.content = content;

		baixarEAbrirPDF(this.tituloArquivoExportacao, documentDefinition);
	}

	exportarCSV() {
		const tableData = this.getDadosTabelaParaExportacao(this.relatorios, false);
		DocumentExporter.gerarCSV(tableData, this.tituloArquivoExportacao);
	}

	exportarTXT() {
		const tableData = this.getDadosTabelaParaExportacao(this.relatorios, false);
		let txtData = '';

		if (tableData.length > 0) {
			const colunas = tableData[0];

			tableData.slice(1).forEach(dadoRelatorio => {
				colunas.forEach((coluna: string, index: number) => {
					const valor = dadoRelatorio[index];
					txtData += `${coluna}: ${valor ?? '-'}\n`;
				});

				txtData += '\n';
			});
		}

		DocumentExporter.gerarTXT(txtData, this.tituloArquivoExportacao);
	}

	async exportarXLSX() {
		const tipoDesvio = this.inputs.form.get(FormularioRelatorio.TIPO)?.value;
		const tipoAgrupamento = this.inputs.form.get(
			FormularioRelatorio.AGRUPAMENTO
		)?.value;
		const periodo = this.inputs.form.get(
			FormularioRelatorio.PERIODO_BUSCA
		)?.value;

		const colorPalette = {
			c1: '2C3639',
			c2: 'FFFFFF',
			c3: '000000',
			c4: 'EEEEEE',
		};

		const rowStyle = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
		};
		const headerStyle = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
		};
		const headerStyleCenter = {
			backgroundColor: colorPalette.c4,
			color: colorPalette.c1,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};
		const rowStyleCenter = {
			backgroundColor: colorPalette.c2,
			color: colorPalette.c3,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const title = {
			backgroundColor: colorPalette.c2,
			whiteSpace: 'pre',
			color: colorPalette.c3,
			bold: true,
			alignment: {
				horizontal: 'center',
				vertical: 'center',
				wrapText: 1,
			} as ExcelTable.DataModel.AlignmentOption,
		};

		const headers = [
			tipoAgrupamento === 'MUNICIPIO_POSTO'
				? {
						label: 'municipio',
						text: 'Município/Posto',
						size: 48,
					}
				: tipoAgrupamento === 'MUNICIPIO'
					? {
							label: 'municipio',
							text: 'Município',
							size: 48,
						}
					: tipoAgrupamento === 'MUNICIPIO_POSTO'
						? {
								label: 'municipio',
								text: 'Município/Posto',
								size: 48,
							}
						: tipoAgrupamento === 'MICRORREGIAO'
							? {
									label: 'microrregiao',
									text: 'Microrregião',
									size: 48,
								}
							: tipoAgrupamento === 'MESORREGIAO'
								? {
										label: 'mesorregiao',
										text: 'Mesorregião',
										size: 48,
									}
								: tipoAgrupamento === 'BACIA_SUBBACIA'
									? {
											label: 'bacia',
											text: 'Bacia/Sub-bacia',
											size: 48,
										}
									: tipoAgrupamento === 'REGIAO_PLUVIOMETRICA'
										? {
												label: 'regiaoPluviometrica',
												text: 'Região pluviométrica',
												size: 48,
											}
										: {
												label: 'subBacia',
												text: 'Sub-bacia',
												size: 48,
											},
		];

		headers.push({
			label: 'data',
			text: 'Data',
			size: 24,
		});

		tipoDesvio === 'DESVIO_MILIMETRO'
			? headers.push({
					label: 'desvioMilimetro',
					text: 'Desvio (mm)',
					size: 24,
				})
			: headers.push({
					label: 'desvioPorcentagem',
					text: 'Desvio (%)',
					size: 24,
				});

		const dataExcel = {
			styles: {
				headerStyle,
				headerStyleCenter,
				rowStyle,
				rowStyleCenter,
				title,
			},
			sheet: [
				{
					shiftTop: 3,
					images: [
						{
							url: 'assets/images/cabecalho/cabeçalho_excel.png',
							from: 'A1',
							to: 'C3',
						},
					],
					styleCellCondition(
						data: any,
						object: any,
						rowIndex: number,
						colIndex: number,
						fromHeader: boolean,
						styleKeys: string[]
					) {
						if (data && data.label) {
							return 'headerStyleCenter';
						} else {
							if (colIndex === 0 && tipoAgrupamento === 'MUNICIPIO_POSTO') {
								return 'rowStyle';
							} else {
								return 'rowStyleCenter';
							}
						}
					},
					headers: headers,
					data: this.retornarDesviosParaXLSX(),
					columns: [
						{ key: 'municipio' },
						{
							key:
								tipoDesvio === 'DESVIO_MILIMETRO'
									? 'desvioMilimetro'
									: 'desvioPorcentagem',
							style: { numFmt: '0.0' },
						},
					],
					title: {
						consommeRow: 3,
						consommeCol: 3,
						text: `${this.tituloArquivoExportacao}`,
						styleId: 'title',
					},
				},
			],
			fileName: this.tituloArquivoExportacao,
		};

		ExcelTable.generateExcel(dataExcel);
	}
	async exportarPNG() {
		if (!this.grafico) {
			this.toastr.error(
				'Não é possível exportar PNG pois nenhum gráfico foi gerado.'
			);
			return;
		}

		try {
			const chartBase64 = await criarImagemBase64FromChart(this.grafico);
			const baseImg = await this.carregarImagem(chartBase64);

			const canvas = document.createElement('canvas');
			canvas.width = baseImg.width;
			canvas.height = baseImg.height;
			const ctx = canvas.getContext('2d')!;

			ctx.fillStyle = '#FFFFFF';
			ctx.fillRect(0, 0, canvas.width, canvas.height);

			ctx.drawImage(baseImg, 0, 0, canvas.width, canvas.height);

			try {
				const margem = canvas.width * 0.02;

				const larguraLogoAesa = canvas.width * 0.08;
				const larguraLogoGoverno = canvas.width * 0.1; // aumentei só essa

				const logoAesa = await this.carregarImagem(
					'assets/images/logos/logoAesa.svg'
				);

				const alturaLogoAesa =
					(larguraLogoAesa / logoAesa.width) * logoAesa.height;

				const xLogoAesa = margem;

				const yLogoAesa = canvas.height - alturaLogoAesa - margem;

				ctx.drawImage(
					logoAesa,
					xLogoAesa,
					yLogoAesa,
					larguraLogoAesa,
					alturaLogoAesa
				);

				const logoGoverno = await this.carregarImagem(
					'assets/images/logos/governo_pb.png'
				);

				const alturaLogoGoverno =
					(larguraLogoGoverno / logoGoverno.width) * logoGoverno.height;

				const xLogoGoverno = canvas.width - larguraLogoGoverno - margem;

				const yLogoGoverno = canvas.height - alturaLogoGoverno - margem;

				ctx.drawImage(
					logoGoverno,
					xLogoGoverno,
					yLogoGoverno,
					larguraLogoGoverno,
					alturaLogoGoverno
				);
			} catch (e) {
				console.error('[Desvio PNG] Erro ao desenhar as logos:', e);
			}

			const dataUrl = canvas.toDataURL('image/png', 1);
			const nomeArquivo =
				this.tituloArquivoExportacao || this.fileName || 'grafico-desvio';

			const a = document.createElement('a');
			a.href = dataUrl;
			a.download = `${nomeArquivo}.png`;
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
		} catch (e) {
			console.error(e);
			this.toastr.error('Falha ao exportar PNG.');
		}
	}
}
